"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import classNames from "classnames";
import { signIn } from "next-auth/react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

import { SAMLLogin } from "@calcom/web/modules/auth/components/SAMLLogin";
import { ErrorCode } from "@calcom/features/auth/lib/ErrorCode";
import {
  LastUsed,
  useLastUsed,
} from "@calcom/web/modules/auth/hooks/useLastUsed";
import {
  HOSTED_CAL_FEATURES,
  WEBAPP_URL,
  WEBSITE_URL,
} from "@calcom/lib/constants";
import { emailRegex } from "@calcom/lib/emailSchema";
import { getSafeRedirectUrl } from "@calcom/lib/getSafeRedirectUrl";
import { useCompatSearchParams } from "@calcom/lib/hooks/useCompatSearchParams";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { trpc } from "@calcom/trpc/react";
import { Alert } from "@calcom/ui/components/alert";
import { Button } from "@calcom/ui/components/button";
import { EmailField, PasswordField } from "@calcom/ui/components/form";

import type { inferSSRProps } from "@lib/types/inferSSRProps";

declare global {
  interface Window {
    SchedlyNative?: { isNative: boolean; biometric: { isAvailable: () => Promise<boolean>; login: () => Promise<{ success: boolean }> } };
  }
}

import AddToHomescreen from "@components/AddToHomescreen";
import BackupCode from "@components/auth/BackupCode";
import TwoFactor from "@components/auth/TwoFactor";
import AuthContainer from "@components/ui/AuthContainer";

import type { getServerSideProps } from "@server/lib/auth/login/getServerSideProps";

interface LoginValues {
  email: string;
  password: string;
  totpCode: string;
  backupCode: string;
  csrfToken: string;
}

function SsoEmailView({
  ssoEmail,
  setSsoEmail,
  ssoError,
  setSsoError,
  setSsoMode,
  samlTenantID,
  samlProductID,
  setLastUsed,
  t,
}: {
  ssoEmail: string;
  setSsoEmail: (v: string) => void;
  ssoError: string | null;
  setSsoError: (v: string | null) => void;
  setSsoMode: (v: boolean) => void;
  samlTenantID: string;
  samlProductID: string;
  setLastUsed: (v: string) => void;
  t: (key: string) => string;
}) {
  const ssoEmailSchema = z.string().regex(emailRegex, "Please enter a valid email");
  const mutation = trpc.viewer.public.samlTenantProduct.useMutation({
    onSuccess: async (data) => {
      setLastUsed("saml");
      await signIn("saml", {}, { tenant: data.tenant, product: data.product });
    },
    onError: (err) => {
      setSsoError(t(err.message));
    },
  });

  const handleSsoSubmit = () => {
    const parsed = ssoEmailSchema.safeParse(ssoEmail);
    if (!parsed.success) {
      setSsoError(parsed.error.errors[0]?.message || "Invalid email");
      return;
    }
    setSsoError(null);

    if (!HOSTED_CAL_FEATURES) {
      setLastUsed("saml");
      signIn("saml", {}, { tenant: samlTenantID, product: samlProductID });
      return;
    }

    mutation.mutate({ email: ssoEmail });
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <EmailField
          id="sso-email"
          label={t("email")}
          placeholder="you@company.com"
          value={ssoEmail}
          onChange={(e) => {
            setSsoEmail(e.target.value);
            if (ssoError) setSsoError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSsoSubmit();
            }
          }}
        />
      </div>
      {ssoError && <Alert severity="error" message={ssoError} />}
      <Button
        color="primary"
        className="w-full justify-center"
        loading={mutation.isPending}
        onClick={handleSsoSubmit}>
        {t("continue")}
      </Button>
    </div>
  );
}

const GoogleIcon = () => (
  <img
    className="mr-2 w-4 h-4 text-subtle"
    src="/google-icon-colored.svg"
    alt="Continue with Google Icon"
  />
);
export type PageProps = inferSSRProps<typeof getServerSideProps>;
export default function Login({
  csrfToken,
  isGoogleLoginEnabled,
  isSAMLLoginEnabled,
  samlTenantID,
  samlProductID,
  totpEmail,
}: PageProps) {
  const searchParams = useCompatSearchParams();
  const { t } = useLocale();
  const router = useRouter();
  const formSchema = z
    .object({
      email: z
        .string()
        .min(1, `${t("error_required_field")}`)
        .regex(emailRegex, `${t("enter_valid_email")}`),
      ...(totpEmail
        ? {}
        : { password: z.string().min(1, `${t("error_required_field")}`) }),
    })
    // Passthrough other fields like totpCode
    .passthrough();
  const methods = useForm<LoginValues>({ resolver: zodResolver(formSchema) });
  const { register, formState } = methods;
  const [twoFactorRequired, setTwoFactorRequired] = useState(
    !!totpEmail || false
  );
  const [twoFactorLostAccess, setTwoFactorLostAccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastUsed, setLastUsed] = useLastUsed();
  const [magicLinkMode, setMagicLinkMode] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [ssoMode, setSsoMode] = useState(false);
  const [ssoEmail, setSsoEmail] = useState("");
  const [ssoError, setSsoError] = useState<string | null>(null);

  const errorMessages: { [key: string]: string } = {
    // [ErrorCode.SecondFactorRequired]: t("2fa_enabled_instructions"),
    // Don't leak information about whether an email is registered or not
    [ErrorCode.IncorrectEmailPassword]: t("incorrect_email_password"),
    [ErrorCode.IncorrectTwoFactorCode]: `${t("incorrect_2fa_code")} ${t(
      "please_try_again"
    )}`,
    [ErrorCode.InternalServerError]: `${t("something_went_wrong")} ${t(
      "please_try_again_and_contact_us"
    )}`,
    [ErrorCode.ThirdPartyIdentityProviderEnabled]: t(
      "account_created_with_identity_provider"
    ),
    [ErrorCode.UserAccountSuspended]:
      "Your account has been suspended due to a policy violation. Please check your email for details or contact support at info@schedly.io to request a review.",
  };

  let callbackUrl = searchParams?.get("callbackUrl") || "";

  if (/"\//.test(callbackUrl)) callbackUrl = callbackUrl.substring(1);

  // If not absolute URL, make it absolute
  if (!/^https?:\/\//.test(callbackUrl)) {
    callbackUrl = `${WEBAPP_URL}/${callbackUrl}`;
  }

  const safeCallbackUrl = getSafeRedirectUrl(callbackUrl);

  callbackUrl = safeCallbackUrl || "";

  const signupUrl = callbackUrl
    ? `/signup?redirect=${encodeURIComponent(callbackUrl)}`
    : `/signup`;

  const LoginFooter = (
    <Link href={signupUrl} className="font-medium text-brand-500">
      {t("dont_have_an_account")}
    </Link>
  );

  const TwoFactorFooter = (
    <>
      <Button
        onClick={() => {
          if (twoFactorLostAccess) {
            setTwoFactorLostAccess(false);
            methods.setValue("backupCode", "");
          } else {
            setTwoFactorRequired(false);
            methods.setValue("totpCode", "");
          }
          setErrorMessage(null);
        }}
        StartIcon="arrow-left"
        color="minimal"
      >
        {t("go_back")}
      </Button>
      {!twoFactorLostAccess ? (
        <Button
          onClick={() => {
            setTwoFactorLostAccess(true);
            setErrorMessage(null);
            methods.setValue("totpCode", "");
          }}
          StartIcon="lock"
          color="minimal"
        >
          {t("lost_access")}
        </Button>
      ) : null}
    </>
  );

  const ExternalTotpFooter = (
    <Button
      onClick={() => {
        window.location.replace("/");
      }}
      color="minimal"
    >
      {t("cancel")}
    </Button>
  );

  const [biometricReady, setBiometricReady] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);

  useEffect(() => {
    const bridge = window.SchedlyNative;
    if (!bridge?.isNative) return;
    bridge.biometric
      .isAvailable()
      .then((available) => setBiometricReady(available))
      .catch(() => {});
  }, []);

  const onSubmit = async (values: LoginValues) => {
    setErrorMessage(null);
    // telemetry.event(telemetryEventTypes.login, collectPageParameters());
    const res = await signIn<"credentials">("credentials", {
      ...values,
      callbackUrl,
      redirect: false,
    });
    if (!res) setErrorMessage(errorMessages[ErrorCode.InternalServerError]);
    // we're logged in! let's do a hard refresh to the desired url
    else if (!res.error) {
      setLastUsed("credentials");
      router.push(callbackUrl);
    } else if (res.error === ErrorCode.SecondFactorRequired)
      setTwoFactorRequired(true);
    else if (res.error === ErrorCode.IncorrectBackupCode)
      setErrorMessage(t("incorrect_backup_code"));
    else if (res.error === ErrorCode.MissingBackupCodes)
      setErrorMessage(t("missing_backup_codes"));
    // fallback if error not found
    else setErrorMessage(errorMessages[res.error] || t("something_went_wrong"));
  };

  const onMagicLinkSubmit = async () => {
    const email = methods.getValues("email");
    if (!email || !emailRegex.test(email)) {
      setErrorMessage("Please enter a valid email address");
      return;
    }
    setErrorMessage(null);
    setMagicLinkLoading(true);
    try {
      const res = await signIn("email", {
        email,
        callbackUrl,
        redirect: false,
      });
      if (res?.error) {
        setErrorMessage("Unable to send magic link. Please try again.");
      } else {
        setMagicLinkSent(true);
        setLastUsed("credentials");
      }
    } catch {
      setErrorMessage("Something went wrong. Please try again.");
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const { data, isPending, error } =
    trpc.viewer.public.ssoConnections.useQuery();

  useEffect(
    function refactorMeWithoutEffect() {
      if (error) {
        setErrorMessage(error.message);
      }
    },
    [error]
  );

  const displaySSOLogin = HOSTED_CAL_FEATURES
    ? true
    : isSAMLLoginEnabled && !isPending && data?.connectionExists;

  return (
    <div>
      <AuthContainer
        showLogo
        visualVariant="login"
        heading={twoFactorRequired ? t("2fa_code") : magicLinkMode ? (magicLinkSent ? "" : "Magic Link Sign In") : ssoMode ? "Single Sign-On" : t("welcome_back")}
        subheading={!twoFactorRequired && !magicLinkMode ? (ssoMode ? "Enter your work email to continue with SSO" : "Sign in to your account to continue") : undefined}
        backButton={
          ssoMode && !twoFactorRequired ? (
            <Button
              color="minimal"
              className="auth-back-btn"
              StartIcon="arrow-left"
              onClick={() => {
                setSsoMode(false);
                setSsoError(null);
                setSsoEmail("");
              }}>
              {t("back")}
            </Button>
          ) : undefined
        }
        footerText={
          twoFactorRequired
            ? !totpEmail
              ? TwoFactorFooter
              : ExternalTotpFooter
            : process.env.NEXT_PUBLIC_DISABLE_SIGNUP !== "true" &&
              searchParams?.get("register") !== "false"
            ? LoginFooter
            : null
        }
      >
        <FormProvider {...methods}>
          {!twoFactorRequired && !magicLinkMode && ssoMode && (
            <SsoEmailView
              ssoEmail={ssoEmail}
              setSsoEmail={setSsoEmail}
              ssoError={ssoError}
              setSsoError={setSsoError}
              setSsoMode={setSsoMode}
              samlTenantID={samlTenantID}
              samlProductID={samlProductID}
              setLastUsed={setLastUsed}
              t={t}
            />
          )}
          {!twoFactorRequired && !magicLinkMode && !ssoMode && (
            <>
              <div className="flex flex-col gap-3">
                {biometricReady && (
                  <Button
                    color="primary"
                    className="justify-center w-full"
                    disabled={biometricLoading}
                    loading={biometricLoading}
                    StartIcon="fingerprint"
                    onClick={async (e) => {
                      e.preventDefault();
                      setBiometricLoading(true);
                      try {
                        const bridge = window.SchedlyNative;
                        if (!bridge) return;
                        const result = await bridge.biometric.login();
                        if (result?.success) {
                          window.location.href = searchParams?.get("callbackUrl") || "/services";
                        }
                      } catch {
                        // biometric failed — fall through to password
                      } finally {
                        setBiometricLoading(false);
                      }
                    }}>
                    Sign in with Face ID / Touch ID
                  </Button>
                )}
                {isGoogleLoginEnabled && (
                  <Button
                    color="primary"
                    className="justify-center w-full"
                    disabled={formState.isSubmitting}
                    data-testid="google"
                    CustomStartIcon={<GoogleIcon />}
                    onClick={async (e) => {
                      e.preventDefault();
                      setLastUsed("google");
                      await signIn("google", {
                        callbackUrl,
                      });
                    }}
                  >
                    <span>{t("signin_with_google")}</span>
                    {lastUsed === "google" && <LastUsed />}
                  </Button>
                )}
                {displaySSOLogin && (
                  <Button
                    StartIcon="lock"
                    color="secondary"
                    data-testid="samlAndOidc"
                    className="flex w-full justify-center"
                    disabled={formState.isSubmitting}
                    onClick={(e) => {
                      e.preventDefault();
                      setSsoMode(true);
                    }}>
                    <span>{t("signin_with_saml_oidc")}</span>
                    {lastUsed === "saml" && <LastUsed />}
                  </Button>
                )}
              </div>
              {(isGoogleLoginEnabled || displaySSOLogin) && (
                <div style={{ margin: "20px 0" }}>
                  <div className="flex items-center gap-4">
                    <div className="grow border-t" style={{ borderColor: "var(--cal-border)" }} />
                    <span style={{ fontSize: 13, color: "var(--cal-text-muted)", fontWeight: 500 }}>
                      {t("or").toLocaleLowerCase()}
                    </span>
                    <div className="grow border-t" style={{ borderColor: "var(--cal-border)" }} />
                  </div>
                </div>
              )}
            </>
          )}

          {ssoMode ? null : magicLinkMode ? (
            <div className="stack-y-6">
              {magicLinkSent ? (
                <div className="text-center space-y-4">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                    <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-emphasis font-semibold text-lg">Check your email</h3>
                    <p className="text-default text-sm mt-1">
                      We sent a sign-in link to <strong>{methods.getValues("email")}</strong>
                    </p>
                    <p className="text-subtle text-xs mt-2">
                      Click the link in the email to sign in. The link expires in 10 minutes.
                    </p>
                  </div>
                  <Button
                    color="secondary"
                    className="justify-center w-full"
                    onClick={() => {
                      setMagicLinkSent(false);
                      setMagicLinkMode(false);
                      setErrorMessage(null);
                    }}
                  >
                    Back to sign in
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-default text-sm text-center">
                    Enter your email and we&apos;ll send you a sign-in link. No password needed.
                  </p>
                  <EmailField
                    id="magic-email"
                    label={t("email_address")}
                    defaultValue={searchParams?.get("email") as string}
                    placeholder="john.doe@example.com"
                    required
                    autoComplete="email"
                    {...register("email")}
                  />
                  {errorMessage && <Alert severity="error" title={errorMessage} />}
                  <Button
                    color="primary"
                    className="justify-center w-full"
                    disabled={magicLinkLoading}
                    loading={magicLinkLoading}
                    onClick={onMagicLinkSubmit}
                  >
                    Send magic link
                  </Button>
                  <Button
                    color="minimal"
                    className="justify-center w-full"
                    onClick={() => {
                      setMagicLinkMode(false);
                      setErrorMessage(null);
                    }}
                  >
                    Sign in with password instead
                  </Button>
                </>
              )}
            </div>
          ) : (
            <form
              onSubmit={methods.handleSubmit(onSubmit)}
              noValidate
              data-testid="login-form"
            >
              <div>
                <input
                  defaultValue={csrfToken || undefined}
                  type="hidden"
                  hidden
                  {...register("csrfToken")}
                />
              </div>
              <div className="flex flex-col gap-4">
                <div
                  className={classNames("flex flex-col gap-4", {
                    hidden: twoFactorRequired,
                  })}
                >
                  <EmailField
                    id="email"
                    label={t("email_address")}
                    defaultValue={
                      totpEmail || (searchParams?.get("email") as string)
                    }
                    placeholder="john.doe@example.com"
                    required
                    autoComplete="email"
                    {...register("email")}
                  />
                  <div className="relative">
                    <PasswordField
                      id="password"
                      autoComplete="current-password"
                      required={!totpEmail}
                      className="mb-0"
                      {...register("password")}
                    />
                    <div className="absolute -top-[2px] ltr:right-0 rtl:left-0">
                      <Link
                        href="/auth/forgot-password"
                        tabIndex={-1}
                        style={{ fontSize: 13, fontWeight: 600, color: "#6366f1", textDecoration: "none" }}
                      >
                        {t("forgot")}
                      </Link>
                    </div>
                  </div>
                </div>

                {twoFactorRequired ? (
                  !twoFactorLostAccess ? (
                    <TwoFactor center />
                  ) : (
                    <BackupCode center />
                  )
                ) : null}

                {errorMessage && <Alert severity="error" title={errorMessage} />}
                <Button
                  type="submit"
                  color="primary"
                  disabled={formState.isSubmitting}
                  className="justify-center w-full"
                >
                  <span>{twoFactorRequired ? t("submit") : t("sign_in")}</span>
                  {lastUsed === "credentials" && !twoFactorRequired && (
                    <span className="absolute right-3 text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>{t("last_used")}</span>
                  )}
                </Button>
              </div>
              {!twoFactorRequired && (
                <div style={{ marginTop: 20 }}>
                  <div className="flex items-center gap-4" style={{ marginBottom: 12 }}>
                    <div className="grow border-t" style={{ borderColor: "var(--cal-border)" }} />
                    <span style={{ fontSize: 13, color: "var(--cal-text-muted)", fontWeight: 500 }}>
                      {t("or").toLocaleLowerCase()}
                    </span>
                    <div className="grow border-t" style={{ borderColor: "var(--cal-border)" }} />
                  </div>
                  <Button
                    color="secondary"
                    className="justify-center w-full"
                    onClick={(e) => {
                      e.preventDefault();
                      setMagicLinkMode(true);
                      setErrorMessage(null);
                    }}
                    StartIcon="sparkles"
                  >
                    Sign in with magic link
                  </Button>
                </div>
              )}
            </form>
          )}
        </FormProvider>
      </AuthContainer>
      <AddToHomescreen />
    </div>
  );
}
